/**
 * IconButton: an icon-only button that always has an accessible name.
 *
 *   <IconButton icon="mdi:close" label="Close" onClick={onClose} />
 *   <IconButton icon="mdi:code-tags" label="Show code" variant="secondary" />
 *   <IconButton icon="mdi:dots-vertical" label="More actions for this run" size="sm" />
 *
 * label    required. Used as aria-label and as the native tooltip (title) unless tooltip={false}.
 * icon     Iconify name or a node.
 * variant  ghost (default) | secondary (bordered) | primary | danger
 * size     sm 32px | md 36px | lg 40px. On touch screens every size is at least 40px.
 * pressed  set for toggle buttons; renders aria-pressed.
 * type defaults to "button".
 */
import { forwardRef, ReactNode, ButtonHTMLAttributes } from "react";
import { Icon } from "@iconify/react";
import { ButtonSpinner } from "./Button";

type Variant = "ghost" | "secondary" | "primary" | "danger";
type Size = "sm" | "md" | "lg";

const VARIANTS: Record<Variant, string> = {
  ghost: "text-fg-muted hover:text-fg hover:bg-black/5 dark:hover:bg-white/10",
  secondary:
    "text-fg-secondary bg-surface border border-line-strong hover:bg-surface-muted hover:text-fg dark:bg-white/5 dark:hover:bg-white/10",
  primary: "text-white bg-primary hover:bg-primary-hover",
  danger: "text-danger-text hover:bg-danger-subtle",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 w-8 text-base pointer-coarse:h-10 pointer-coarse:w-10",
  md: "h-9 w-9 text-lg pointer-coarse:h-10 pointer-coarse:w-10",
  lg: "h-10 w-10 text-xl",
};

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> {
  icon: string | ReactNode;
  label: string;
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  pressed?: boolean;
  tooltip?: boolean;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    icon,
    label,
    variant = "ghost",
    size = "md",
    loading = false,
    pressed,
    tooltip = true,
    type = "button",
    className = "",
    disabled,
    ...rest
  },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={tooltip ? label : undefined}
      aria-pressed={pressed}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center shrink-0 rounded-control transition-colors duration-150 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50 ${VARIANTS[variant]} ${SIZES[size]} ${pressed ? "bg-black/5 text-fg dark:bg-white/10" : ""} ${className}`}
      {...rest}
    >
      {loading ? (
        <ButtonSpinner />
      ) : typeof icon === "string" ? (
        <Icon icon={icon} aria-hidden="true" />
      ) : (
        icon
      )}
    </button>
  );
});

export default IconButton;
